import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_b2b_clients";

export const defaultB2BClients = [
  {
    id: "b2b-cli-demo-1",
    companyName: "Restaurant Le Cèdre",
    contactName: "Antoine Mansour",
    siret: "80234567800019",
    tva: "FR32802345678",
    address: "24 Avenue des Champs, 91000 Évry-Courcouronnes",
    deliveryAddress: "24 Avenue des Champs (Accès livraison arrière), 91000 Évry-Courcouronnes",
    phone: "01 69 47 00 12",
    email: "contact@lecedreresto.fr",
    active: true,
    createdAt: "2026-01-15T09:00:00Z",
  },
  {
    id: "b2b-cli-demo-2",
    companyName: "Boulangerie Saint-Germain",
    contactName: "Sarah Bernard",
    siret: "91234567800025",
    tva: "FR45912345678",
    address: "12 Rue de Paris, 91100 Corbeil-Essonnes",
    deliveryAddress: "12 Rue de Paris, 91100 Corbeil-Essonnes",
    phone: "01 64 96 11 22",
    email: "direction@boulangerie-sg.fr",
    active: true,
    createdAt: "2026-02-01T11:30:00Z",
  },
];

export const normalizeB2BClient = (c = {}) => ({
  id: c.id || `b2b-cli-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  companyName: c.companyName || c.company_name || "",
  contactName: c.contactName || c.contact_name || "",
  siret: c.siret || "",
  tva: c.tva || "",
  address: c.address || "",
  deliveryAddress: c.deliveryAddress || c.delivery_address || "",
  phone: c.phone || "",
  email: c.email || "",
  active: c.active !== false,
  createdAt: c.createdAt || c.created_at || new Date().toISOString(),
});

const readCache = () => {
  if (typeof window === "undefined") return [...defaultB2BClients];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...defaultB2BClients];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return [...defaultB2BClients];
    return parsed.map(normalizeB2BClient);
  } catch (err) {
    console.warn("Failed to read B2B clients from localStorage:", err);
    return [...defaultB2BClients];
  }
};

const writeCache = (clients) => {
  const normalized = (Array.isArray(clients) ? clients : []).map(normalizeB2BClient);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (err) {
      console.warn("Failed to write B2B clients to localStorage:", err);
    }
  }
  return normalized;
};

export const getB2BClients = () => readCache();

export const loadB2BClients = async () => {
  const cached = readCache();
  if (!isSupabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase
      .from("b2b_clients")
      .select("*")
      .order("company_name", { ascending: true });

    if (error || !data) {
      if (error) console.error("Failed to load B2B clients from Supabase:", error.message);
      return cached;
    }

    return writeCache(
      data.map((row) => ({
        id: row.id,
        companyName: row.company_name || "",
        contactName: row.contact_name || "",
        siret: row.siret || "",
        tva: row.tva || "",
        address: row.address || "",
        deliveryAddress: row.delivery_address || "",
        phone: row.phone || "",
        email: row.email || "",
        active: row.active !== false,
        createdAt: row.created_at || new Date().toISOString(),
      }))
    );
  } catch (err) {
    console.error("Failed to load B2B clients from Supabase:", err);
    return cached;
  }
};

export const saveB2BClient = async (client) => {
  const normalized = normalizeB2BClient(client);
  const all = readCache();
  const idx = all.findIndex((c) => c.id === normalized.id);
  const updated = idx >= 0 ? all.map((c, i) => (i === idx ? normalized : c)) : [normalized, ...all];
  const saved = writeCache(updated);

  if (!isSupabaseConfigured || !supabase) return saved;
  try {
    await supabase.from("b2b_clients").upsert(
      {
        id: normalized.id,
        company_name: normalized.companyName,
        contact_name: normalized.contactName,
        siret: normalized.siret,
        tva: normalized.tva,
        address: normalized.address,
        delivery_address: normalized.deliveryAddress,
        phone: normalized.phone,
        email: normalized.email,
        active: normalized.active,
        created_at: normalized.createdAt,
      },
      { onConflict: "id" }
    );
  } catch (err) {
    console.error("Failed to save B2B client to Supabase:", err);
  }
  return saved;
};

export const deleteB2BClient = async (id) => {
  const all = readCache();
  const updated = all.filter((c) => c.id !== id);
  writeCache(updated);

  if (!isSupabaseConfigured || !supabase) return true;
  try {
    const { error } = await supabase.from("b2b_clients").delete().eq("id", id);
    if (error) throw error;
  } catch (err) {
    console.error("Failed to delete B2B client from Supabase:", err.message);
    throw err;
  }
  return true;
};
